import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import { Button } from '@paypalcorp/pp-react-buttons';
import { BodyText } from '@paypalcorp/pp-react-text';
import { ChevronCircleUpIcon, EditIcon } from '@paypalcorp/pp-react-icons';
import { change } from 'redux-form';
import { BTN_FACTORY_V3_FORM } from '../../clientConstants';
import { useDropzone } from 'react-dropzone';
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE,
} from '../../../utility/constants';
import { Alert } from '@paypalcorp/pp-react-alert';
import _get from 'lodash/get';
import { grey300 } from '@paypalcorp/pp-react-colors';

import getLocaleMessages from '../../../buttons/utils/localesMessages';
import { trackEventWithData } from '../../utilsV3/analytics';
import { FPTI_SPEC, SCREEN } from '../../utilsV3/fptiConstants';
const errors = getLocaleMessages('buttonsV3/fieldErrors');

const CropImage = ({
  imgUrl,
  getContent,
  uploadImage,
  closeModal,
  widthBanner,
  dispatch,
  isMobile,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [cropAreaPixels, setCropAreaPixels] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const [fade, setFade] = useState(true);
  const [urlSrc, setUrlSrc] = useState(imgUrl);

  const saveButtonImage = getContent('modal.saveButtonImage');
  const editButton = getContent('modal.editButton');
  const dragImage = getContent('modal.dragImage');
  const [dropzoneError, setDropzoneError] = useState('');

  const resizeImage = (original, imgName) => {
    const img = new Image();
    img.src = original;
    img.crossOrigin = 'Anonymous';
    img.onload = (el) => {
      let imgObj = el.target;
      let elem = document.createElement('canvas');
      elem.width = widthBanner;
      elem.height = (widthBanner * imgObj.height) / imgObj.width;

      let ctx = elem.getContext('2d');
      ctx.drawImage(el.target, 0, 0, elem.width, elem.height);
      const srcEncoded = ctx.canvas.toDataURL(el.target, 'image/jpeg', 0);
      setUrlSrc(srcEncoded);
    };
  };

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    accept: ALLOWED_IMAGE_TYPES,
    maxSize: MAX_IMAGE_SIZE,
    onDrop: useCallback((acceptedFiles) => {
      setDropzoneError('');
      acceptedFiles.map((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const binaryStr = reader.result;
          resizeImage(binaryStr, file.name);
        };
        reader.readAsDataURL(file);
      });
    }, []),
    onDropRejected: (rejections) => {
      try {
        const errorCode = _get(rejections[0], 'errors[0].code', 'default');
        switch (errorCode) {
          case 'file-too-large':
            setDropzoneError(errors.dropzone.maxSize);
            break;
          case 'file-invalid-type':
            setDropzoneError(errors.dropzone.notSupported);
            break;
          default:
            setDropzoneError(errors.dropzone.tryAgain);
            break;
        }
      } catch (e) {
        setDropzoneError(errors.dropzone.tryAgain);
      }
    },
  });

  const createImage = () =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = urlSrc;
    });

  const getCroppedImage = async () => {
    const image = await createImage();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    canvas.width = safeArea;
    canvas.height = safeArea;

    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // draw rotated image and store data.
    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5
    );
    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = cropAreaPixels.width;
    canvas.height = cropAreaPixels.height;

    // paste generated rotate image with correct offsets for x,y crop values.
    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width * 0.5 - cropAreaPixels.x),
      Math.round(0 - safeArea / 2 + image.height * 0.5 - cropAreaPixels.y)
    );

    // As Base64 string
    return canvas.toDataURL('image/jpeg');
  };

  const handleImageUpload = async (url) => {
    return new Promise((resolve, reject) => {
      uploadImage(url)
        .then((response) => {
          if (response.ok) {
            return resolve(response);
          }

          let error = {
            processingStage: 'IMAGE_UPLOAD_ERROR',
            response,
          };
          return reject(error);
        })
        .catch((error) => {
          return reject({ ...error, processingStage: 'IMAGE_UPLOAD_ERROR' });
        });
    });
  };

  const saveCroppedImage = useCallback(async () => {
    trackEventWithData(FPTI_SPEC.UPLOAD_BACKGOURND_IMG_MODAL_SAVE_CL);

    const img = await getCroppedImage();
    try {
      setShowSpinner(true);
      let response = await handleImageUpload(img);
      if (response.success) {
        setShowSpinner(false);
        dispatch(change(BTN_FACTORY_V3_FORM, 'bannerImageUrl', response.src));
        closeModal();
      } else {
        setShowSpinner(false);
        setDropzoneError(errors.dropzone.tryAgain);
        trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
          error_code: 'IMAGE_UPLOAD_ERROR',
          error_desc: 'banner_image',
          error_detail: 'try again',
          screen: SCREEN.EXPERIENCE_SETUP,
        });
      }
    } catch (e) {
      setShowSpinner(false);
      setDropzoneError(errors.dropzone.tryAgain);
      trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
        error_code: 'IMAGE_UPLOAD_ERROR',
        error_desc: 'banner_image',
        error_detail: 'try again',
        screen: SCREEN.EXPERIENCE_SETUP,
      });
    }
  });

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCropAreaPixels(croppedAreaPixels);
  }, []);

  const fadeOut = () => {
    setFade(false);
  };
  const fadeIn = () => {
    setFade(true);
  };

  return (
    <>
      <div
        onMouseDown={fadeOut}
        onMouseUp={fadeIn}
        onMouseLeave={fadeIn}
        onTouchStart={fadeOut}
        onTouchEnd={fadeIn}
        className={`crop-area-container`}
      >
        <BodyText
          className="crop-area-message"
          id="crop-area-message"
          style={{ opacity: fade ? 1 : 0 }}
        >
          {<ChevronCircleUpIcon size="sm" className="text-icon" />}
          {dragImage}
        </BodyText>

        <Cropper
          image={urlSrc}
          crop={crop}
          aspect={!isMobile ? 77 / 25 : 3 / 1}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          objectFit="horizontal-cover"
          showGrid={false}
          style={{
            containerStyle: {
              borderRadius: '20px 20px 0px 0px',
              border: `1px solid ${grey300}`,
            },
          }}
        />
      </div>
      <div {...getRootProps()} tabIndex="-1">
        <input type="file" id="upload" {...getInputProps()} />
        <Button
          className="button-link"
          htmlFor="upload"
          type="button"
          iconComponent={EditIcon}
          tertiary
        >
          {editButton}
        </Button>
      </div>
      {dropzoneError && (
        <Alert type="warning" className="dropzone-error">
          {dropzoneError}
        </Alert>
      )}

      <Button
        onClick={saveCroppedImage}
        btnState={showSpinner ? 'processing' : 'initial'}
        className="banner-save-img"
      >
        {saveButtonImage}
      </Button>
    </>
  );
};

CropImage.propTypes = {
  imgUrl: PropTypes.string,
  getContent: PropTypes.func,
  uploadImage: PropTypes.func,
  closeModal: PropTypes.func,
  dispatch: PropTypes.func,
  widthBanner: PropTypes.number,
  isMobile: PropTypes.bool,
};

export default CropImage;
